import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Clock,
  Layers,
  MessageSquareWarning,
  Search,
  UserPlus,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Layout from '@/components/Layout';
import { MY_AUDITED_CPM_CAMPAIGNS } from '@/queries/queries';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCents } from '@/lib/format';
import { CampaignCpmRateList } from '@/components/campaigns/CampaignCpmRateList';
import { describeCampaignCpmRates } from '@/lib/campaigns/describeCampaignCpmRates';
import { formatDateTime } from '@/lib/format/datetime';
import CampaignBudgetBar from '@/components/CampaignBudgetBar';
import { CampaignAvatar } from '@/components/campaigns/CampaignAvatar';
import { CampaignBanner } from '@/components/campaigns/CampaignBanner';
import { CampaignCategoryBadges } from '@/components/campaigns/CampaignCategoryBadges';
import {
  AUDIT_STATUS_TABS,
  countAuditCampaignsByTab,
  filterAuditCampaigns,
  sortAuditCampaignsByPriority,
  summarizeAuditKpis,
  type AuditCampaignSummary,
  type AuditStatusTab,
} from '@/lib/campaignAudit/organizeAuditCampaigns';
import { cn } from '@/lib/utils';
import { RankingCampaignSummary } from '@/components/campaigns/RankingCampaignSummary';

// Production introspection is off, so this page reads the hand-written
// MY_AUDITED_CPM_CAMPAIGNS selection instead of generated types.
type AuditCampaign = AuditCampaignSummary & {
  campaign_type?: string | null;
  ranking_config?: unknown;
  logo_url?: string | null;
  banner_url?: string | null;
  categories?: string[] | null;
  youtube_channels?: Array<{ name?: string | null; handle?: string | null; thumbnail?: string | null }> | null;
  start_date?: string | null;
  currency?: string | null;
  cpm_rate_cents: number;
  cpm_platform_rates?: { platform: string; cpm_rate_cents: number }[] | null;
  allowed_platforms?: string[] | null;
  max_payout_per_clip_cents?: number | null;
  budget_total_cents?: number | null;
  budget_spent_cents?: number | null;
  budget_remaining_cents?: number | null;
  budget_pending_cents?: number | null;
  submissions_count?: number | null;
  approved_submissions_count?: number | null;
  disapproved_submissions_count?: number | null;
};

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/40',
  paused: 'bg-amber-500/10 text-amber-400 border-amber-500/40',
  scheduled: 'bg-sky-500/10 text-sky-400 border-sky-500/40',
  draft: 'bg-muted text-muted-foreground border-border',
  ended: 'bg-purple-500/10 text-purple-300 border-purple-500/30',
};

function KpiCard({ icon: Icon, label, value, hint, tone }: {
  icon: LucideIcon;
  label: string;
  value: number;
  hint: string;
  tone: string;
}) {
  const { i18n } = useTranslation();
  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
          <div className={cn('w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0', tone)}>
            <Icon className="w-4 h-4" aria-hidden="true" />
          </div>
        </div>
        <p className="mt-2 text-3xl font-bold tabular-nums">{value.toLocaleString(i18n.language)}</p>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

function AuditCampaignCard({ campaign: c }: { campaign: AuditCampaign }) {
  const { t, i18n } = useTranslation();
  const fmt = (n: number) => n.toLocaleString(i18n.language);
  const currency = c.currency || 'BRL';
  const total = c.budget_total_cents || 0;
  const remaining = c.budget_remaining_cents ?? Math.max(0, total - (c.budget_spent_cents || 0));
  const spent = Math.max(0, total - remaining);
  // A finished campaign's empty budget is expected, not a warning.
  const budgetLow = c.status !== 'ended' && total > 0 && remaining / total < 0.25;
  const hasSubmissions = (c.submissions_count || 0) > 0;
  const pending = c.pending_submissions_count || 0;
  const approved = c.approved_submissions_count || 0;
  const disapproved = c.disapproved_submissions_count || 0;
  const appeals = c.pending_appeals_count || 0;
  const channelRequests = c.pending_channel_requests_count || 0;

  return (
    <Link to={`/campaign-audit/${c.id}`} className="block" data-testid="campaign-audit-card">
      <Card className="overflow-hidden hover:shadow-md hover:border-emerald-500/30 transition">
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0 sm:flex-1">
              <CampaignBanner
                src={c.banner_url}
                className="hidden sm:block w-32 flex-shrink-0 rounded-md border border-border/50"
              />
              <CampaignAvatar campaign={c} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg sm:text-xl font-bold truncate">{c.name}</h2>
                  <span
                    className={cn(
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border flex-shrink-0',
                      STATUS_BADGE[c.status] || STATUS_BADGE.draft,
                    )}
                  >
                    {t(`campaignAudit.status.${c.status}`)}
                  </span>
                </div>
                {c.description && (
                  <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{c.description}</p>
                )}
                <CampaignCategoryBadges categories={c.categories} className="mt-1.5" />
                {c.status === 'scheduled' && c.start_date && (
                  <p className="text-xs text-sky-400 mt-1 inline-flex items-center gap-1">
                    <CalendarClock className="w-3.5 h-3.5" aria-hidden="true" />
                    {t('campaignAudit.startsAt', { date: formatDateTime(c.start_date) })}
                  </p>
                )}
              </div>
            </div>

            {c.campaign_type === 'ranking' ? <RankingCampaignSummary config={c.ranking_config} currency={currency} /> : <div className="flex gap-4 text-sm flex-wrap">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  {t('campaign.rateLabel')}
                </p>
                {(() => {
                  const rates = describeCampaignCpmRates(c);
                  return rates.kind === 'single' ? (
                    <p className="font-bold">
                      {formatCents(rates.rateCents, currency)}
                      <span className="text-xs text-muted-foreground font-normal">
                        {' / '}
                        {t('campaign.per1kViews')}
                      </span>
                    </p>
                  ) : (
                    <CampaignCpmRateList rates={rates.rates} currency={currency} />
                  );
                })()}
              </div>
              {c.max_payout_per_clip_cents ? (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {t('campaign.clipLimitLabel')}
                  </p>
                  <p className="font-bold">{formatCents(c.max_payout_per_clip_cents, currency)}</p>
                </div>
              ) : null}
            </div>}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-2">
                <span>
                  <span className="font-semibold text-foreground">{formatCents(remaining, currency)}</span>
                  {' '}
                  {t('campaign.remaining')}
                </span>
                {budgetLow && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 text-[10px] font-bold uppercase tracking-wider">
                    <Clock className="w-2.5 h-2.5" aria-hidden="true" />
                    {t('campaign.budgetLow')}
                  </span>
                )}
              </span>
              <span>{t('campaign.budgetGoal', { amount: formatCents(total, currency) })}</span>
            </div>
            <CampaignBudgetBar
              totalCents={total}
              spentCents={spent}
              pendingCents={c.budget_pending_cents || 0}
              currency={currency}
              size="sm"
            />
          </div>

          <div className="pt-3 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap text-xs">
              {!hasSubmissions ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted border border-border text-muted-foreground font-semibold">
                  {t('campaignAudit.noSubmissions')}
                </span>
              ) : pending > 0 ? (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-300 font-bold"
                  data-testid="campaign-audit-pending"
                >
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" aria-hidden="true" />
                  {t('campaignAudit.pendingClips', { count: pending, value: fmt(pending) })}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-semibold">
                  <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                  {t('campaignAudit.allReviewed')}
                </span>
              )}
              {hasSubmissions && (
                <span className="text-muted-foreground">
                  {t('campaignAudit.approvedClips', { count: approved, value: fmt(approved) })}
                </span>
              )}
              {disapproved > 0 && (
                <span className="text-rose-400">
                  {t('campaignAudit.disapprovedClips', { count: disapproved, value: fmt(disapproved) })}
                </span>
              )}
              {appeals > 0 && (
                <span className="text-amber-400 font-medium">
                  {t('campaignAudit.pendingAppeals', { count: appeals, value: fmt(appeals) })}
                </span>
              )}
              {channelRequests > 0 && (
                <span className="text-sky-400 font-medium">
                  {t('campaignAudit.pendingChannelRequests', { count: channelRequests, value: fmt(channelRequests) })}
                </span>
              )}
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm text-primary font-semibold flex-shrink-0">
              {t('campaignAudit.reviewAction')}
              {pending > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-primary/15 text-[11px] font-bold tabular-nums">
                  {fmt(pending)}
                </span>
              )}
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function CampaignAuditListPage() {
  const { t } = useTranslation();
  const { data, loading } = useQuery(MY_AUDITED_CPM_CAMPAIGNS);
  const [tab, setTab] = useState<AuditStatusTab>('all');
  const [query, setQuery] = useState('');

  const campaigns = useMemo(
    () => (data?.adminCpmCampaigns?.data || []) as AuditCampaign[],
    [data],
  );
  const counts = useMemo(() => countAuditCampaignsByTab(campaigns), [campaigns]);
  const kpis = useMemo(() => summarizeAuditKpis(campaigns), [campaigns]);
  const visible = useMemo(
    () => sortAuditCampaignsByPriority(filterAuditCampaigns(campaigns, { tab, query })) as AuditCampaign[],
    [campaigns, tab, query],
  );

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold">
              {t('campaignAudit.title')}
            </h1>
            <p className="text-muted-foreground mt-2 text-lg">
              {t('campaignAudit.subtitle')}
            </p>
          </div>

          {loading ? (
            <p className="text-sm text-muted-foreground">
              {t('campaignAudit.loading')}
            </p>
          ) : campaigns.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('campaignAudit.empty')}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="campaign-audit-kpis">
                <KpiCard
                  icon={Clock}
                  label={t('campaignAudit.kpis.pendingClips')}
                  value={kpis.pendingClips}
                  hint={t('campaignAudit.kpis.pendingClipsHint')}
                  tone="bg-amber-500/10 border-amber-500/20 text-amber-400"
                />
                <KpiCard
                  icon={MessageSquareWarning}
                  label={t('campaignAudit.kpis.pendingAppeals')}
                  value={kpis.pendingAppeals}
                  hint={t('campaignAudit.kpis.pendingAppealsHint')}
                  tone="bg-rose-500/10 border-rose-500/20 text-rose-400"
                />
                <KpiCard
                  icon={UserPlus}
                  label={t('campaignAudit.kpis.pendingChannelRequests')}
                  value={kpis.pendingChannelRequests}
                  hint={t('campaignAudit.kpis.pendingChannelRequestsHint')}
                  tone="bg-sky-500/10 border-sky-500/20 text-sky-400"
                />
                <KpiCard
                  icon={Layers}
                  label={t('campaignAudit.kpis.activeCampaigns')}
                  value={kpis.active}
                  hint={t('campaignAudit.kpis.activeCampaignsHint', {
                    total: kpis.total,
                    scheduled: kpis.scheduled,
                    draft: kpis.draft,
                  })}
                  tone="bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                />
              </div>

              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                <Tabs value={tab} onValueChange={(value) => setTab(value as AuditStatusTab)}>
                  <div className="overflow-x-auto -mx-1 px-1">
                    <TabsList>
                      {AUDIT_STATUS_TABS.map((key) => (
                        <TabsTrigger key={key} value={key} className="whitespace-nowrap">
                          {t(`campaignAudit.tabs.${key}`)}
                          <span className="ml-1.5 text-xs tabular-nums opacity-70">{counts[key]}</span>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>
                </Tabs>
                <div className="relative lg:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('campaignAudit.searchPlaceholder')}
                    aria-label={t('campaignAudit.searchPlaceholder')}
                    className="pl-9 pr-9"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      aria-label={t('campaignAudit.clearSearch')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>

              <p className="text-sm text-muted-foreground" data-testid="campaign-audit-found">
                {t('campaignAudit.found', { count: visible.length })}
              </p>

              {visible.length === 0 ? (
                <div className="text-center py-16 rounded-xl border border-dashed border-border">
                  <p className="font-semibold">{t('campaignAudit.noMatchTitle')}</p>
                  <p className="text-sm text-muted-foreground mt-1">{t('campaignAudit.noMatchBody')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {visible.map((c) => (
                    <AuditCampaignCard key={c.id} campaign={c} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </Layout>
  );
}
